import { type FC } from "react";

import { Card } from "@/components/ui/Card";

interface SpO2CardProperties {
  average?: number;
  breathingDisturbanceIndex?: number;
  className?: string;
}

/**
 * SpO2 Gesundheitsbereiche definieren
 *
 * @param value - SpO2 value as percentage
 *
 * @returns Status object with bgColor, color, and status
 *
 */
function getSpO2Status(value?: number): {
  bgColor: string;
  color: string;
  status: string;
} {
  if (!value) {
    return {
      bgColor: "bg-neutral-700",
      color: "text-neutral-400",
      status: "unknown",
    };
  }

  if (value >= 95) {
    return {
      bgColor: "bg-green-900/20",
      color: "text-green-400",
      status: "excellent",
    };
  }
  if (value >= 90) {
    return {
      bgColor: "bg-blue-900/20",
      color: "text-blue-400",
      status: "good",
    };
  }
  if (value >= 85) {
    return {
      bgColor: "bg-yellow-900/20",
      color: "text-yellow-400",
      status: "fair",
    };
  }
  return { bgColor: "bg-red-900/20", color: "text-red-400", status: "poor" };
}

/**
 * SpO2Card - Kompakte Blutsauerstoff-Anzeige
 *
 * Features:
 * - Verwendet globale Card-Komponente für konsistentes Styling
 * - Farbkodierung basierend auf Gesundheitsbereichen
 * - Kompakte Darstellung aller wichtigen Werte
 * - Spezielle SpO2-Logik und Gesundheitsbereiche
 *
 * @param root0
 *
 * @param root0.average
 *
 * @param root0.breathingDisturbanceIndex
 *
 * @param root0.className
 *
 * @returns {JSX.Element} SpO2 card component
 *
 */
export const SpO2Card: FC<SpO2CardProperties> = ({
  average,
  breathingDisturbanceIndex,
  className = "",
}) => {
  const spO2Status = getSpO2Status(average);

  return (
    <Card className={className} padding="sm" variant="default">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <h3 className="text-sm font-medium text-neutral-200">SpO2</h3>
          <p className="text-xs text-neutral-400">Blutsauerstoff</p>
        </div>
        <div
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${spO2Status.bgColor} ${spO2Status.color}`}
        >
          {spO2Status.status === "unknown" ? "N/A" : spO2Status.status}
        </div>
      </div>

      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center">
          <div className="text-lg font-bold text-neutral-100">
            {average ? `${average.toFixed(2)}%` : "N/A"}
          </div>
          <div className="text-xs text-neutral-400 ml-1">Ø</div>
        </div>

        {breathingDisturbanceIndex !== undefined && (
          <div className="text-right">
            <div className="text-xs text-neutral-400">Atmungsstörung</div>
            <div className="text-sm font-medium text-neutral-200">
              {breathingDisturbanceIndex.toFixed(1)}
            </div>
          </div>
        )}
      </div>

      {breathingDisturbanceIndex !== undefined && (
        <div className="w-full bg-neutral-700 rounded-full h-1">
          <div
            className="bg-blue-500 h-1 rounded-full transition-all duration-300"
            style={{
              width: `${String(Math.min((breathingDisturbanceIndex / 10) * 100, 100))}%`,
            }}
          />
        </div>
      )}
    </Card>
  );
};
